// OnboardingLoader — fullscreen "coach is calculating your baseline"
// transition between the wizard and the dashboard.
//
// Why this exists:
//   The earlier flow dropped the user into the chat immediately after
//   onboarding and tried to generate the gift plan in the background.
//   That had two UX problems: (1) the user was looking at a chat
//   bubble instead of the artefact they actually wanted (their plan),
//   and (2) the generation could disappear silently if Chat unmounted
//   mid-fetch. State 1 of the new architecture says: block on plan
//   generation, show it full-screen, fade into the dashboard when done.
//
// Scope (State 1 only):
//   - Generates BOTH current-week AND next-week plans up front.
//   - For multi-sport athletes (running + cycling) we fire all four
//     combinations in parallel (Promise.allSettled, not Promise.all —
//     a single failure must NOT cancel the others).
//   - On full success: onComplete() → App.js navigates to weekly-plan.
//   - On full failure (after retry): show the friendly error screen
//     with the [נסה לייצר תוכנית שוב] button. The happy-path UI never
//     shows that button — it appears only when we've actually failed.
//
// What this component intentionally does NOT do:
//   - Background generation while inside the app (that's State 2,
//     belongs in WeeklyPlan).
//   - Chat-driven modifications (State 3, still read-only via CTAs).

import React, { useState, useEffect, useRef, useCallback } from 'react';
import authFetch from '../utils/authFetch';
import { Wrench, Sparkle } from '@phosphor-icons/react';

// Inline constant — matches the pattern used by WeeklyPlan.jsx and the
// other page-level files. There's no shared config/api module yet.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

// One slot per (sport, week) combination — drives the progress display.
function buildTasks(sports) {
  const list = [];
  (sports && sports.length ? sports : ['running']).forEach((sport) => {
    list.push({ sport, week: 'current' });
    list.push({ sport, week: 'next' });
  });
  return list;
}

function sportLabel(sport) {
  return sport === 'cycling' ? 'רכיבה' : 'ריצה';
}

function weekLabel(week) {
  return week === 'next' ? 'שבוע הבא' : 'השבוע';
}

export default function OnboardingLoader({ userId, sports, profile, onComplete, onBack }) {
  // tasks is static after mount — sports doesn't change during the loader.
  const tasksRef = useRef(buildTasks(sports));
  const [statuses, setStatuses] = useState(() => tasksRef.current.map(() => 'pending'));
  // 'running' | 'error' | 'done'. 'done' triggers a brief fade before
  // navigation so the success-state feels deliberate rather than abrupt.
  const [phase, setPhase] = useState('running');
  // Strictly informational — surfaced in the error screen so the user
  // can mention it in support. Empty in the happy path.
  const [lastError, setLastError] = useState('');

  // Run one (sport, week) fetch. Returns true on success, false otherwise.
  // We treat any non-200 OR data.success===false as a failure — the
  // V2 endpoint signals limit_reached / blocked / no_profile via the
  // success flag inside a 200 body, so we have to inspect both.
  const runOne = useCallback(async (task, idx) => {
    setStatuses(prev => { const next = [...prev]; next[idx] = 'in_progress'; return next; });
    const ctrl = new AbortController();
    // 180s per call — matches WeeklyPlan's manual button. Render cold-
    // start + Gemini fallback + retry can push past 90s, and we'd
    // rather wait than report a false failure.
    const timer = setTimeout(() => ctrl.abort('timeout'), 180_000);
    try {
      const resp = await authFetch(
        `${API_BASE_URL}/weekly-plan/generate-v2?user_id=${userId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ week_type: task.week, sport_type: task.sport }),
          signal: ctrl.signal,
        },
      );
      if (!resp.ok) {
        setLastError(`HTTP ${resp.status} on ${task.sport}/${task.week}`);
        setStatuses(prev => { const next = [...prev]; next[idx] = 'error'; return next; });
        return false;
      }
      const data = await resp.json().catch(() => ({}));
      if (data?.success === false) {
        setLastError(`${data.error || 'unknown'} on ${task.sport}/${task.week}`);
        setStatuses(prev => { const next = [...prev]; next[idx] = 'error'; return next; });
        return false;
      }
      setStatuses(prev => { const next = [...prev]; next[idx] = 'done'; return next; });
      return true;
    } catch (err) {
      setLastError(`${err.name || 'Error'}: ${task.sport}/${task.week}`);
      setStatuses(prev => { const next = [...prev]; next[idx] = 'error'; return next; });
      return false;
    } finally {
      clearTimeout(timer);
    }
  }, [userId]);

  // Kick off all tasks in parallel. Retry only refires the tasks that
  // failed — we don't want to double-bill the user's daily message
  // quota by regenerating plans that already succeeded.
  const fire = useCallback(async () => {
    setPhase('running');
    setLastError('');
    const tasks = tasksRef.current;
    const failures = [];
    statuses.forEach((s, i) => { if (s !== 'done') failures.push(i); });
    const indices = failures.length ? failures : tasks.map((_, i) => i);
    // Reset only the indices we're about to fire.
    setStatuses(prev => {
      const next = [...prev];
      indices.forEach(i => { next[i] = 'pending'; });
      return next;
    });
    const results = await Promise.allSettled(indices.map(i => runOne(tasks[i], i)));
    const anySuccess = results.some(r => r.status === 'fulfilled' && r.value === true);
    const allSuccess = results.every(r => r.status === 'fulfilled' && r.value === true);
    if (allSuccess) {
      setPhase('done');
      // Tiny fade-out delay so the green checkmarks register before the
      // transition. 600ms feels intentional without dragging.
      setTimeout(() => onComplete && onComplete(), 600);
      return;
    }
    // 'error' shows the friendly retry screen. We treat 'partial success'
    // (some plans saved, others didn't) the same way — the user can
    // either retry the failed ones or skip to the dashboard and see
    // what got saved.
    setPhase('error');
    // Suppress lint warning: we intentionally don't depend on statuses
    // because we read the latest synchronously inside the closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runOne]);

  useEffect(() => {
    fire();
    // Run once on mount. Retry is triggered via the button, not effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doneCount = statuses.filter(s => s === 'done').length;
  const totalCount = statuses.length;
  const percent = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  // ── Error screen ──────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-surface border border-border rounded-[16px] p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed text-accent flex items-center justify-center mb-4"><Wrench size={30} weight="bold" /></div>
          <h2 className="text-2xl font-bold text-text mb-3">
            אופס, משהו השתבש בסנכרון עם המאמן
          </h2>
          <p className="text-text-body text-sm mb-6 leading-relaxed">
            ייתכן שהשרת בעומס. בלי דאגה — לחיצה אחת ואנחנו מנסים שוב.
          </p>
          {lastError && (
            <p className="text-text-muted text-[11px] mb-4 font-mono break-all">{lastError}</p>
          )}
          <button
            onClick={fire}
            className="w-full h-12 rounded-full text-base btn-primary transition-all"
          >
            נסה לייצר תוכנית שוב
          </button>
          {onBack && (
            <button
              onClick={onBack}
              className="w-full mt-3 py-2 rounded-xl text-text-muted text-sm hover:text-text transition-all"
            >
              חזרה
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Running / done screen ─────────────────────────────────────────
  const name = profile?.name || 'אתלט';
  return (
    <div className={`min-h-screen bg-bg flex items-center justify-center p-6 transition-opacity duration-500 ${phase === 'done' ? 'opacity-0' : 'opacity-100'}`}>
      <div className="max-w-md w-full bg-surface border border-border rounded-[16px] p-8 text-center">
        {/* Pulsing icon — gives the screen a "something's happening"
            feel even before per-task statuses arrive. */}
        <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed text-accent flex items-center justify-center mb-4 animate-pulse"><Sparkle size={30} weight="bold" /></div>
        <h2 className="text-2xl font-bold text-text mb-2">
          המאמן מחשב את התכנית האישית שלך, {name}
        </h2>
        <p className="text-text-body text-sm mb-6">
          בונה את השבוע הנוכחי ואת השבוע הבא — לוקח כ-30-90 שניות
        </p>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-2 bg-pressed rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-accent transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="text-text-body text-xs font-medium">{doneCount} / {totalCount} תכניות מוכנות</div>
        </div>

        {/* Per-task status — only worth showing when there are 3+ tasks
            (i.e. multi-sport). With 2 tasks the progress bar alone is
            already clear. */}
        {totalCount >= 3 && (
          <div className="space-y-2 text-right">
            {tasksRef.current.map((t, i) => {
              const s = statuses[i];
              const icon = s === 'done' ? '✅' : s === 'in_progress' ? '⏳' : s === 'error' ? '⚠️' : '·';
              const color = s === 'done' ? 'text-text-body' : s === 'error' ? 'text-text-body' : 'text-text-muted';
              return (
                <div key={`${t.sport}-${t.week}`} className={`text-sm flex items-center justify-end gap-2 ${color}`}>
                  <span>{sportLabel(t.sport)} · {weekLabel(t.week)}</span>
                  <span>{icon}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
